import React, { Key, useMemo, useState } from 'react';
import { Table, Pagination, Collapse, Empty, Typography, Spin, Button, Popover, Popconfirm, Input, Space, message } from 'antd';
import moment from 'moment';
import { assigneVehicleNumberToTrip, importLoginextTripsMultileg } from '../deliveryApi';

// Separate component for vehicle number assignment popover
const VehicleNumberPopover = ({ tripName, onSuccess }: { tripName: string; onSuccess: () => void }) => {
  const [vehicleNum, setVehicleNum] = useState('');
  const [assigning, setAssigning] = useState(false);

  const handleAssign = async () => {
    if (!vehicleNum) {
      message.error("Vehicle number is required");
      return;
    }

    setAssigning(true);
    try {
      await assigneVehicleNumberToTrip({ vehicle_number: vehicleNum, trip_name: tripName });
      message.success("Vehicle number assigned successfully");
      setVehicleNum('');
      onSuccess();
    } catch (e) {
      message.error("Failed to assign vehicle number");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Space>
      <Input
        placeholder="Vehicle Number"
        size="small"
        value={vehicleNum}
        onChange={(e) => setVehicleNum(e.target.value)}
        onPressEnter={handleAssign}
      />
      <Button type="primary" size="small" onClick={handleAssign} loading={assigning}>
        Assign
      </Button>
    </Space>
  );
};

interface DeliveryTableProps {
  loading: boolean;
  data: any[];
  setData: (data: any[]) => void;
  columns: any[];
  deliveriesColumns: any[];
  deliveriesPage: number;
  setDeliveriesPage: (page: number) => void;
  deliveriesCount: number;
  deliveriesView: boolean;
  etasView: boolean;
  page: number;
  total_pos: number;
  fetchDeliveries: (params: any) => void;
  queryParams: any;
  etasSearch?: string;
}

const TRIPS_PER_PAGE = 10;

export const DeliveryTable = ({
  loading,
  data,
  setData,
  columns,
  deliveriesColumns,
  deliveriesPage,
  setDeliveriesPage,
  deliveriesCount,
  deliveriesView,
  etasView,
  page,
  total_pos,
  fetchDeliveries,
  queryParams,
  etasSearch = '',
}: DeliveryTableProps) => {

  // Group data by trip_name for etasView
  const groupedTrips = useMemo(() => {
    if (!etasView) return {};

    return data.reduce((acc: Record<string, any[]>, delivery) => {
      const tripName = delivery.trip_name ?? "Unassigned";
      if (!acc[tripName]) {
        acc[tripName] = [];
      }
      acc[tripName].push(delivery);
      return acc;
    }, {});
  }, [data, etasView]);

  // Filter grouped trips based on search
  const filteredGroupedTrips = useMemo(() => {
    if (!etasView) return {};

    const q = etasSearch.toLowerCase();
    if (!q) return groupedTrips;

    const filtered: Record<string, any[]> = {};
    Object.keys(groupedTrips).forEach((tripName) => {
      // Check if trip name matches
      if (tripName.toLowerCase().includes(q)) {
        filtered[tripName] = groupedTrips[tripName];
        return;
      }
      // Check if any delivery in the trip matches
      const matchingDeliveries = groupedTrips[tripName].filter((d: any) =>
        d?.lead_id?.toLowerCase().includes(q) ||
        d?.po?.po_number?.toLowerCase().includes(q)
      );
      if (matchingDeliveries.length > 0) {
        filtered[tripName] = matchingDeliveries;
      }
    });
    return filtered;
  }, [groupedTrips, etasSearch, etasView]);

  // State for trip pagination in etasView
  const [tripPage, setTripPage] = useState(1);

  // Paginate trips (10 trips per page)
  const paginatedTripNames = useMemo(() => {
    if (!etasView) return [];
    const allTripNames = Object.keys(filteredGroupedTrips);
    const startIndex = (tripPage - 1) * TRIPS_PER_PAGE;
    const endIndex = startIndex + TRIPS_PER_PAGE;
    return allTripNames.slice(startIndex, endIndex);
  }, [filteredGroupedTrips, tripPage, etasView]);

  const totalTrips = useMemo(() => {
    if (!etasView) return 0;
    return Object.keys(filteredGroupedTrips).length;
  }, [filteredGroupedTrips, etasView]);


  const reImportAllTrips = async () => {
    const allTripNames = Object.keys(groupedTrips).filter(
      (tripName) => tripName !== "Unassigned"
    );
    if (allTripNames.length === 0) {
      message.info("No trips to re-import");
      return;
    }
    message.loading({ content: "Re-importing all trips...", key: "reImportAllTrips" });
    const result = await importLoginextTripsMultileg(allTripNames);
    if (result) {
      message.success({
        content: "Re-importing all trips in the background. Please refresh after a few minutes!",
        key: "reImportAllTrips",
      });
      fetchDeliveries({ keepCurrentSearch: true });
    } else {
      message.error({ content: "Failed to re-import trips", key: "reImportAllTrips" });
    }
  };

  if (etasView) {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spin />
        </div>
      );
    }

    if (totalTrips === 0) {
      return <Empty description="No deliveries found" />;
    }

    return (
      <>
        <div className="mb-3">
          <Popconfirm
            title="This will reset all deliveries to track from loginext. Are you sure?"
            onConfirm={reImportAllTrips}
          >
            <Button type="default">Re-import All Trips</Button>
          </Popconfirm>
        </div>
        <Collapse defaultActiveKey={etasSearch ? paginatedTripNames : []}>
          {paginatedTripNames.map((tripName) => {
            const tripDeliveries = filteredGroupedTrips[tripName];
            const vehicleNumber = tripDeliveries[0]?.vehicle_number;
            const driverNumber = tripDeliveries[0]?.driver_number;

            return (
              <Collapse.Panel
                header={
                  <div className="d-flex justify-content-between" style={{ width: '100%' }}>
                    <span style={{ flexGrow: 1, minWidth: '15%' }}>
                      <Typography.Text strong className="mb-0 d-block" style={{ fontSize: 12 }}>
                        Trip
                      </Typography.Text>
                      {tripName}
                    </span>
                    <span style={{ flexGrow: 1, minWidth: '15%' }}>
                      <Typography.Text strong className="mb-0 d-block" style={{ fontSize: 12 }}>
                        Vehicle Number
                      </Typography.Text>
                      {vehicleNumber || <span className="text-muted">Not set</span>}
                    </span>
                    <span style={{ flexGrow: 1, minWidth: '15%' }}>
                      <Typography.Text strong className="mb-0 d-block" style={{ fontSize: 12 }}>
                        Driver Number
                      </Typography.Text>
                      {driverNumber || <span className="text-muted">Not set</span>}
                    </span>
                    <span style={{ flexGrow: 1, minWidth: '10%' }}>
                      <Typography.Text strong className="mb-0 d-block" style={{ fontSize: 12 }}>
                        Total POs
                      </Typography.Text>
                      {tripDeliveries.length}
                    </span>
                    <span style={{ flexGrow: 1, minWidth: '20%' }}>
                      <Typography.Text strong className="mb-0 d-block" style={{ fontSize: 12 }}>
                        Assigned To
                      </Typography.Text>
                      {Array.from(
                        new Set(tripDeliveries.map((x: any) => x?.delivery_person_name))
                      ).filter(Boolean).join(", ") || <span className="text-muted">Not assigned</span>}
                    </span>
                  </div>
                }
                key={tripName}
              >
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={tripDeliveries}
                  pagination={false}
                  scroll={{ x: '100%' }}
                />
                {tripName !== "Unassigned" && (
                  <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                    <Button
                      type="default"
                      onClick={async () => {
                        message.loading({ content: "Reimporting trip...", key: "reimportTrip" });
                        const result = await importLoginextTripsMultileg([String(tripName)]);
                        if (result) {
                          message.success({ content: "Trip reimported successfully", key: "reimportTrip" });
                          fetchDeliveries({ keepCurrentSearch: true });
                        } else {
                          message.error({ content: "Failed to reimport trip", key: "reimportTrip" });
                        }
                      }}
                    >
                      Reimport Trip
                    </Button>
                    <Popover
                      content={<VehicleNumberPopover tripName={tripName} onSuccess={() => fetchDeliveries({ keepCurrentSearch: true })} />}
                      trigger="click"
                      title="Assign Vehicle Number"
                    >
                      <Button type="default">
                        Assign Vehicle Number
                      </Button>
                    </Popover>
                  </div>
                )}
              </Collapse.Panel>
            );
          })}
        </Collapse>
        <Pagination
          current={tripPage}
          total={totalTrips}
          pageSize={TRIPS_PER_PAGE}
          className="mt-3"
          onChange={(p) => setTripPage(p)}
          showTotal={(total) => `Total ${total} trips`}
        />
      </>
    );
  }

  if (deliveriesView) {
    // Group deliveries by trip_no for clustering view
    const groupedData = data.reduce((acc: any[], delivery) => {
      const tripNo = delivery?.trip_no;
      if (!tripNo) {
        acc.push({
          key: `no-trip-${delivery.id}`,
          trip_name: "N/A",
          deliveries: [delivery],
          isExpanded: false,
        });
      } else {
        let tripGroup = acc.find(group => group.trip_no === tripNo);
        if (!tripGroup) {
          const firstDelivery = data.find(d => d?.trip_no === tripNo);
          // Standardize trip name format
          const standardizedName = firstDelivery?.trip_name || `Trip ${tripNo}`;
          const formattedTripName = standardizedName.replace(/^TRIP-/i, 'Trip ');
          acc.push({
            key: tripNo,
            trip_name: formattedTripName,
            deliveries: [delivery],
            isExpanded: false,
          });
        } else {
          tripGroup.deliveries.push(delivery);
        }
      }
      return acc;
    }, []);

    const sortedGroups = groupedData.sort((a, b) => {
      if (a.key === 'no-trip') return 1;
      if (b.key === 'no-trip') return -1;
      return String(a.key).localeCompare(String(b.key));
    });

    const flattenedData = sortedGroups.flatMap((group: any) => {
      return group.deliveries.map((delivery: any, deliveryIndex: number) => ({
        ...delivery,
        key: `${group.key}-${deliveryIndex}`,
        _groupKey: group.key,
        _groupName: group.trip_name,
        _isGroupLeader: deliveryIndex === 0,
        _groupSize: group.deliveries.length,
        _isExpanded: group.isExpanded,
      }));
    });

    return (
      <Table
        loading={loading}
        rowKey={"id"}
        columns={deliveriesColumns}
        scroll={{ x: "100%" }}
        dataSource={flattenedData}
        pagination={{
          current: deliveriesPage,
          total: deliveriesCount ?? 1,
          pageSize: 10,
          onChange: setDeliveriesPage,
        }}
        expandable={{
          expandedRowKeys: sortedGroups.filter((g: any) => g.isExpanded).map((g: any) => g.key),
          onExpandedRowsChange: (expandedRows: readonly Key[]) => {
            const updatedGroups = sortedGroups.map((group: any) => ({
              ...group,
              isExpanded: expandedRows.includes(group.key),
            }));
            const updatedFlattenedData = updatedGroups.flatMap((group: any) => 
              group.deliveries.map((delivery: any, deliveryIndex: number) => ({
                ...delivery,
                _isExpanded: expandedRows.includes(group.key),
              }))
            );
            setData(updatedFlattenedData);
          },
          expandedRowRender: (record) => (
            <div style={{ padding: "8px 16px", background: "#f5f5f5", border: "1px solid #d9d9d9", borderRadius: "6px", margin: "8px 0" }}>
               <span style={{ fontWeight: 600 }}>{record._groupName} ({record._groupSize} items)</span>
            </div>
          )
        }}
      />
    );
  }

  return (
    <>
      <Table
        loading={loading}
        rowKey={"id"}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: "100%" }}
        onRow={(record) => ({
          style: {
            background: record?.leg_status === "dispatch_pending" && moment().diff(record?.leg_commited_dispatch_time, "minutes") > 60 ? "#FFB4A3" 
                        : record?.leg_status === "dispatch_pending" && moment().diff(record?.leg_commited_dispatch_time, "minutes") > 20 ? "#FFFFC0" : ""
          },
        })}
      />
      <Pagination
        current={page}
        total={total_pos ?? 1}
        pageSize={10}
        className="mt-3"
        onChange={(p) => fetchDeliveries({ page: p, ...queryParams })}
      />
    </>
  );
};
